import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, AlertCircle, Info, CheckCircle2, ChevronDown, TrendingUp } from 'lucide-react';
export type Bureau = 'Experian' | 'TransUnion' | 'Equifax';
export type Product = 'FICO' | 'Vantage';
export interface CreditScore {
  bureau: Bureau;
  product: Product;
  value: number;
}
export interface BorrowerData {
  name: string;
  type: 'Primary' | 'Co-Borrower';
  scores: CreditScore[];
}
interface CreditScorePanelProps {
  borrower: BorrowerData;
  coBorrower?: BorrowerData;
  loanType: 'Standard' | 'FHA/Streamline';
}
// Pricing buckets — each threshold is the lower bound of a bucket
const PRICING_BUCKETS = [780, 760, 740, 720, 700, 680, 660, 640, 620];
function getPricingBucket(score: number): number {
  for (const threshold of PRICING_BUCKETS) {
    if (score >= threshold) return threshold;
  }
  return 0; // below 620
}
function getBucketLabel(bucket: number): string {
  const index = PRICING_BUCKETS.indexOf(bucket);
  if (bucket === 0) return 'Below 620';
  if (index === 0) return `${bucket}+`;
  return `${bucket}–${PRICING_BUCKETS[index - 1] - 1}`;
}
export function CreditScorePanel({
  borrower,
  coBorrower,
  loanType
}: CreditScorePanelProps) {
  const [expanded, setExpanded] = useState(false);
  const processScores = (data: BorrowerData) => {
    const ficoScores = [...data.scores].filter(s => s.product === 'FICO').sort((a, b) => a.value - b.value);
    const vantageScores = [...data.scores].filter(s => s.product === 'Vantage').sort((a, b) => a.value - b.value);
    const ficoMedian = ficoScores[1];
    const vantageMedian = vantageScores[1];
    const winningProduct = ficoMedian.value >= vantageMedian.value ? 'FICO' : 'Vantage';
    const winningScores = winningProduct === 'FICO' ? ficoScores : vantageScores;
    const winningMedian = winningProduct === 'FICO' ? ficoMedian : vantageMedian;
    const winningHighest = winningScores[2];
    return {
      data,
      ficoMedian,
      vantageMedian,
      winningProduct,
      winningMedian,
      winningHighest
    };
  };
  const b1 = processScores(borrower);
  const b2 = coBorrower ? processScores(coBorrower) : null;
  // Qualifying = lower of the two medians
  let qualifyingBorrower = b1;
  let nonQualifyingBorrower = b2;
  if (b2 && b2.winningMedian.value < b1.winningMedian.value) {
    qualifyingBorrower = b2;
    nonQualifyingBorrower = b1;
  }
  // FHA/Streamline logic
  const isEnhancedWorkflow = loanType === 'FHA/Streamline' && qualifyingBorrower.winningMedian.value < 740;
  const displayScore = isEnhancedWorkflow ? qualifyingBorrower.winningHighest : qualifyingBorrower.winningMedian;
  // Pricing bucket analysis
  const currentBucket = getPricingBucket(qualifyingBorrower.winningMedian.value);
  const altBucket = nonQualifyingBorrower ? getPricingBucket(nonQualifyingBorrower.winningMedian.value) : currentBucket;
  const showPricingImprovement = b2 !== null && altBucket > currentBucket;
  // Get all borrowers to display
  const allBorrowers = b2 ? [b1, b2] : [b1];
  // Get consistent bureau order
  const bureaus: Bureau[] = ['TransUnion', 'Experian', 'Equifax'];
  // Render a product card (FICO or Vantage) with borrowers as side-by-side columns
  const renderProductColumn = (product: Product) => {
    return <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-id="element-6">
        {/* Product header */}
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-200 text-center" data-id="element-7">
          <span className="text-sm font-bold uppercase tracking-wide text-gray-900" data-id="element-8">
            {product}
          </span>
        </div>

        <div className="p-3" data-id="element-9">
          {/* Borrower name headers */}
          <div className="grid gap-2" style={{
          gridTemplateColumns: `auto ${allBorrowers.map(() => '1fr').join(' ')}`
        }} data-id="element-10">
            {/* Empty cell for bureau label column */}
            <div data-id="element-11" />
            {allBorrowers.map(processed => <div key={processed.data.name} className="text-center" data-id="element-12">
                <p className="text-xs font-bold text-gray-900 leading-tight" data-id="element-13">
                  {processed.data.name}
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-14">
                  {processed.data.type}
                </p>
              </div>)}
          </div>

          {/* Score rows by bureau */}
          <div className="mt-2 space-y-0.5" data-id="element-15">
            {bureaus.map(bureau => {
            // Check if any borrower has a selected score in this row
            const rowHasSelection = allBorrowers.some(processed => {
              const isQualifying = processed === qualifyingBorrower;
              const scoreToShow = isQualifying && isEnhancedWorkflow ? processed.winningHighest : processed.winningMedian;
              return scoreToShow.bureau === bureau && scoreToShow.product === product;
            });
            return <div key={bureau} className={`grid gap-2 py-1 px-2 -mx-2 rounded ${rowHasSelection ? 'bg-blue-50 border border-blue-200' : ''}`} style={{
              gridTemplateColumns: `auto ${allBorrowers.map(() => '1fr').join(' ')}`
            }} data-id="element-16">
                  <span className={`text-xs self-center whitespace-nowrap ${rowHasSelection ? 'text-blue-900 font-medium' : 'text-gray-500'}`} data-id="element-17">
                    {bureau}
                  </span>
                  {allBorrowers.map(processed => {
                const isQualifying = processed === qualifyingBorrower;
                const scoreToShow = isQualifying && isEnhancedWorkflow ? processed.winningHighest : processed.winningMedian;
                const score = processed.data.scores.find(s => s.product === product && s.bureau === bureau);
                const isSelected = score && scoreToShow.bureau === bureau && scoreToShow.product === product;
                return <div key={`${processed.data.name}-${bureau}`} className="text-center" data-id="element-18">
                        <span className={`text-sm font-semibold inline-block px-2 py-0.5 ${isSelected ? 'text-blue-900 font-bold' : 'text-gray-700'}`} data-id="element-19">
                          {score?.value ?? '—'}
                        </span>
                      </div>;
              })}
                </div>;
          })}
          </div>

          {/* Median row */}
          <div className="grid gap-2 pt-2 mt-1 border-t border-gray-200" style={{
          gridTemplateColumns: `auto ${allBorrowers.map(() => '1fr').join(' ')}`
        }} data-id="element-20">
            <span className="text-xs text-gray-400 italic self-center" data-id="element-21">
              Median
            </span>
            {allBorrowers.map(processed => <div key={processed.data.name} className="text-center" data-id="element-22">
                <span className="text-xs text-gray-400 font-medium" data-id="element-23">
                  {product === 'FICO' ? processed.ficoMedian.value : processed.vantageMedian.value}
                </span>
              </div>)}
          </div>
        </div>
      </div>;
  };
  return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-24">
      {/* Header */}
      <div className="bg-[#0082CA] border-b border-[#006BA1] px-4 py-3 flex items-center justify-between" data-id="element-25">
        <div className="flex items-center space-x-2" data-id="element-26">
          <ShieldCheck className="w-5 h-5 text-white/80" data-id="element-27" />
          <h2 className="text-sm font-semibold text-white uppercase tracking-wide" data-id="element-28">
            Decision Credit Score
          </h2>
        </div>
        <span className="text-xs text-white/70 font-medium" data-id="element-29">
          Bucket: {getBucketLabel(currentBucket)}
        </span>
      </div>

      {/* Body */}
      <div className="p-6 flex flex-col" data-id="element-30">
        {/* Main Qualifying Score */}
        <div className="flex flex-col items-center text-center" data-id="element-31">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-32">
            Qualifying Score
          </p>

          <div className="relative mt-2" data-id="element-33">
            <span className="text-6xl font-bold text-gray-900 tracking-tight" data-id="element-34">
              {displayScore.value}
            </span>
            {displayScore.value >= 740 ? <CheckCircle2 className="w-6 h-6 text-green-500 absolute -right-8 top-2" data-id="element-35" /> : <AlertCircle className="w-6 h-6 text-amber-500 absolute -right-8 top-2" data-id="element-36" />}
          </div>

          {/* Product displayed prominently */}
          <div className="mt-4 flex flex-col items-center gap-1" data-id="element-37">
            <span className="text-2xl font-bold text-[#0082CA]" data-id="element-38">
              {displayScore.product}
            </span>
            <span className="text-sm text-gray-600" data-id="element-39">{displayScore.bureau}</span>
          </div>

          {b2 && <p className="mt-3 text-xs text-gray-500" data-id="element-40">
              Based on the lower middle score between {b1.data.name} (
              {b1.winningMedian.value}) and {b2.data.name} (
              {b2.winningMedian.value})
            </p>}
        </div>

        {/* Potential Pricing Improvement — only if removing a borrower jumps to a better bucket */}
        {showPricingImprovement && nonQualifyingBorrower && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-id="element-41">
            <div className="flex items-start space-x-3" data-id="element-42">
              <TrendingUp className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-43" />
              <div data-id="element-44">
                <h3 className="text-sm font-semibold text-emerald-900" data-id="element-45">
                  Potential Pricing Improvement
                </h3>
                <p className="text-sm text-emerald-800 mt-1" data-id="element-46">
                  Removing <strong data-id="element-47">{qualifyingBorrower.data.name}</strong> would
                  move the qualifying score from{' '}
                  <strong data-id="element-48">{qualifyingBorrower.winningMedian.value}</strong>{' '}
                  (bucket {getBucketLabel(currentBucket)}) to{' '}
                  <strong data-id="element-49">{nonQualifyingBorrower.winningMedian.value}</strong>{' '}
                  (bucket {getBucketLabel(altBucket)}).
                </p>
                <div className="mt-3 flex items-center space-x-2" data-id="element-50">
                  <div className="px-3 py-1.5 bg-white border border-emerald-200 rounded text-center" data-id="element-51">
                    <p className="text-xs text-gray-500" data-id="element-52">Current</p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-53">
                      {qualifyingBorrower.winningMedian.value}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-54">
                      {getBucketLabel(currentBucket)}
                    </p>
                  </div>
                  <span className="text-emerald-600 font-bold text-lg" data-id="element-55">→</span>
                  <div className="px-3 py-1.5 bg-white border border-emerald-300 rounded text-center ring-1 ring-emerald-200" data-id="element-56">
                    <p className="text-xs text-emerald-600" data-id="element-57">New</p>
                    <p className="text-lg font-bold text-emerald-700" data-id="element-58">
                      {nonQualifyingBorrower.winningMedian.value}
                    </p>
                    <p className="text-xs text-emerald-600" data-id="element-59">
                      {getBucketLabel(altBucket)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Expandable Details Toggle */}
        <button type="button" onClick={() => setExpanded(!expanded)} className="mt-6 w-full flex items-center justify-center space-x-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-60">
          <span data-id="element-61">
            {expanded ? 'Hide score breakdown' : 'View score breakdown'}
          </span>
          <motion.div animate={{
          rotate: expanded ? 180 : 0
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }} data-id="element-62">
            <ChevronDown className="w-4 h-4" data-id="element-63" />
          </motion.div>
        </button>

        {/* Expandable Content */}
        <AnimatePresence initial={false} data-id="element-64">
          {expanded && <motion.div key="details" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} className="w-full overflow-hidden" data-id="element-65">
              <div className="mt-4 text-left space-y-4" data-id="element-66">
                {/* Score Selection Logic */}
                <motion.div initial={{
              y: -8,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.1,
              duration: 0.3
            }} className="bg-blue-50 rounded-md p-3 flex items-start space-x-3" data-id="element-67">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-68" />
                  <div className="text-sm text-blue-900" data-id="element-69">
                    <p className="font-medium mb-1" data-id="element-70">Score Selection Logic</p>
                    {isEnhancedWorkflow ? <p className="text-blue-800" data-id="element-71">
                        FHA/Streamline qualifying score (
                        {qualifyingBorrower.winningMedian.value}) is below 740.
                        Showing the highest {qualifyingBorrower.winningProduct}{' '}
                        score for {qualifyingBorrower.data.name} per enhanced
                        workflow.
                      </p> : <p className="text-blue-800" data-id="element-72">
                        For each borrower, selected the highest median score
                        between FICO and Vantage. The lower middle score between
                        all borrowers determines pricing.
                      </p>}
                  </div>
                </motion.div>

                {/* All Scores */}
                <motion.div initial={{
              y: -8,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.2,
              duration: 0.3
            }} className="bg-gray-50 rounded-md p-4" data-id="element-73">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3" data-id="element-74">
                    All Scores
                  </p>
                  <div className="grid grid-cols-2 gap-4" data-id="element-75">
                    {renderProductColumn('FICO')}
                    {renderProductColumn('Vantage')}
                  </div>
                </motion.div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}